import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Button, Card, Divider, Dropdown, Empty, Input, InputNumber, List, message, Modal, Select, Space, Spin, Switch, Table, Tag, Tooltip, Typography,
} from 'antd';
import { DeleteOutlined, MoreOutlined, PlusOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import api from '../api/client';
import useAuthStore from '../store/authStore';
import {
  ServiceOrder, createOrder, getOrder, setOrderType, updateOrder,
} from '../api/serviceOrders';
import { OilQueueItem, enqueueOrder, finishOrder, getLastExecutors, getQueue, removeFromQueue } from '../api/oilChange';
import { getOneCMarkStatus, getCatalogById } from '../api/onec';
import { markingApi } from '../api/marking';
import { unreceivedApi } from '../api/unreceived';
import type { BufferCode } from '../api/marking';
import useScanTargetStore from '../store/scanTargetStore';
import useScanBufferStore from '../store/scanBufferStore';
import MarkCodeLink from '../components/marking/MarkCodeLink';
import { sellabilityError } from '../components/marking/sellable';
import { addBarcodeProductToLines } from '../components/service-order/barcode';
import type { BarcodeProduct } from '../api/barcodeScans';
// Единая логика с заказ-нарядом (OilChangePage.md §1 п.4/п.8, rev. 2026-09-08 №2):
// черновик, payload, разлив — общие модули; вид отличается только компоновкой
import {
  Draft, DraftLine, ensureTrailingEmpty, isEmptyLine, isInfoRow, isPieceUnit, isVolumeUnit, displayUnit, fmtLiters, fmtLitersFixed, newLine, withBulkInfoRow,
} from '../components/service-order/types';
import { emptyDraft, orderToDraft } from '../components/service-order/draft';
import { enterAsTab, arrowFieldNav, executorSelectKeyDown } from '../components/service-order/enterAsTab';
import { getOrderPostingAvailable, getOrderPrintAvailable, getReceiptFiscalizeAvailable, getTaxInvoiceAvailable } from '../components/service-order/posting';
import { printServiceOrderForm } from '../components/service-order/printing';
import PostAndPayModal from '../components/service-order/PostAndPayModal';
import { DeletionBanner, requestDeletionFlow } from '../components/service-order/deletion';
import { applyOilChangeExecutors, hasMixedServiceExecutors, unionServiceExecutors } from '../components/service-order/executors';
import { buildPayload, formatApiError } from '../components/service-order/buildPayload';
import ShiftClosedModal, { isShiftClosedError } from '../components/service-order/ShiftClosedModal';
import VehiclePicker, { VehiclePickerRef } from '../components/vehicle/VehiclePicker';
import PlateInput from '../components/vehicle/PlateInput';
import {
  applyMarksToMaster, bulkCoverageError, bulkMasterMaxQty, fetchOwnProductMarks, fetchProductMarks,
  findRestorableBulkLines, promoteToBulkMaster, redistributeMaster,
} from '../components/service-order/bulk';
import { CatalogOption, filtersFirst, filterWarehouseOptions, redLast, useOneCCatalogs, SERVICE_NAME_PAID, SERVICE_NAME_FREE, normServiceName, isAutoServiceName, autoServiceFirst } from '../hooks/useOneCCatalogs';

const { Text } = Typography;

// Авто-услуга — две реальные услуги 1С (OilChangePage.md §1 п.5, rev. 2026-09-09):
// масло выбрано → бесплатная замена (0 ₽), иначе платная (1200 ₽, rev. 2026-09-26).
// Константы имён и autoServiceFirst — общие, в hooks/useOneCCatalogs (rev. №40)
const SERVICE_PRICE_DEFAULT = 1200;
// Отображаемое имя авто-услуги (в черновике и в 1С уходит точное имя, §1 п.5)
const displayServiceName = (n?: string | null) =>
  normServiceName(n) === SERVICE_NAME_FREE ? 'Бесплатная замена масла' : (n ?? '');

const QUICK_SERVICES = [
  { key: 'engine_guard', label: 'Защита', name: 'Защита - с/у', price: 550 },
  { key: 'air_filter',   label: 'Воздушный', name: 'Воздушный фильтр- замена', price: 800 },
  { key: 'cabin_filter', label: 'Салонный',  name: 'Фильтр салона - замена', price: 1400 },
  // «Фильтр топливный - замена» — выносной фильтр (вариант «…(со снятием топливного
  // бака)» — погружной, не используется). В регистре цен 1С услуга с ценой 0 (№29)
  { key: 'fuel_filter',  label: 'Топливный', name: 'Фильтр топливный - замена', price: 2000 },
];

// Поле «Исполнители» (§3Г, rev. 2026-09-10 №2): закреплённые первыми в списке
// (матч по фамилии, без хардкода GUID)
const PINNED_EXECUTOR_SURNAMES = ['прудкий', 'селезнев', 'нефедов'];
const pinnedExecutorRank = (label: string): number => {
  const l = label.toLowerCase();
  const i = PINNED_EXECUTOR_SURNAMES.findIndex(s => l.includes(s));
  return i === -1 ? PINNED_EXECUTOR_SURNAMES.length : i;
};

// Иконки масла в карточке очереди (схема §3 rev. 2026-09-11 №2–№3):
// канистра: зелёная — наше масло, синяя — клиент со своим;
// бочка (всегда зелёная) — в заказе есть разлив из бочки, заменяет канистру.
// Контурный стиль с каплей — по референсам пользователя (rev. 2026-09-11 №4)
const OIL_GREEN = '#52c41a';
const OIL_BLUE = '#1677ff';

const OilCanIcon: React.FC<{ ourOil: boolean; color?: string }> = ({ ourOil, color }) => {
  const c = color ?? (ourOil ? OIL_GREEN : OIL_BLUE);
  return (
    <Tooltip title={ourOil ? 'С нашим маслом' : 'Со своим маслом'}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, display: 'block' }}>
        {/* корпус: плоский верх, косое плечико вправо-вниз */}
        <path d="M5.5 21V8.5h5L16 12.5v7a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 21z" />
        {/* крышка с носиком */}
        <path d="M7.5 8.5V5h4v3.5" />
        <path d="M9 5V3.2h1.6V5" />
        {/* капля */}
        <path d="M10.6 13c1.5 1.9 2.3 2.9 2.3 3.9a2.3 2.3 0 1 1-4.6 0c0-1 .8-2 2.3-3.9z" fill={c} stroke="none" />
      </svg>
    </Tooltip>
  );
};

const OilBarrelIcon: React.FC<{ color?: string }> = ({ color }) => {
  const c = color ?? OIL_GREEN;
  return (
  <Tooltip title="Бочковое масло (разлив)">
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, display: 'block' }}>
      {/* крышка-эллипс с пробкой */}
      <ellipse cx="12" cy="4.6" rx="8.2" ry="2.9" />
      <ellipse cx="8.4" cy="4.2" rx="1.4" ry="0.55" fill={c} stroke="none" />
      {/* стенки и дно */}
      <path d="M3.8 4.6v14.8M20.2 4.6v14.8" />
      <path d="M3.8 19.4a8.2 2.9 0 0 0 16.4 0" />
      {/* обручи */}
      <path d="M3.8 9.4a8.2 2.6 0 0 0 16.4 0M3.8 15.4a8.2 2.6 0 0 0 16.4 0" />
      {/* капля */}
      <path d="M12 10.7c1.5 1.9 2.2 2.8 2.2 3.7a2.2 2.2 0 1 1-4.4 0c0-.9.7-1.8 2.2-3.7z" />
    </svg>
  </Tooltip>
  );
};

const OilMarkIcon: React.FC<{ ourOil: boolean; bulk: boolean; color?: string }> = ({ ourOil, bulk, color }) =>
  bulk ? <OilBarrelIcon color={color} /> : <OilCanIcon ourOil={ourOil} color={color} />;

// Правая колонка «В фасовке» (OilChangePage.md §3Б, rev. 2026-09-09 №9)
interface PackagedInfo {
  name: string;
  packages: { volume: number; price: number; in_stock: number }[];
}
const fmtVol = (v: number) => String(Number(v.toFixed(3))).replace('.', ',');

// Моторное масло: тип из каталога, иначе — моторная вязкость в названии (0W-20 и т.п.)
const VISC_RE = /\b\d{1,2}w[-/]?\d{1,2}\b/i;
const NOT_MOTOR_RE = /atf|трансмисс|акпп|мкпп|cvt|dsg|gl-\d|75w|80w|85w|антифриз|гур|тормоз|adblue/i;
const isMotorOilName = (name?: string | null): boolean =>
  !!name && VISC_RE.test(name) && !NOT_MOTOR_RE.test(name);
const isMotorOilOption = (o: CatalogOption): boolean =>
  (o.types || []).some(t => t.toLowerCase().includes('масло моторное')) || isMotorOilName(o.name);

const OilChangePage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuthStore();
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  // «+ Не в очереди» → /oil-change?enqueue=0: при сохранении не ставить в очередь
  // (oil_change_queue.md §3 п.1, rev. 2026-09-11 №3 / OilChangePage.md §3 rev. №14)
  const [searchParams] = useSearchParams();
  const enqueueOnSave = searchParams.get('enqueue') !== '0';

  const [order, setOrder] = useState<ServiceOrder | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  // Барьер «кассовая смена закрыта» (1c.md §1 rev. 21, rev. №47)
  const [shiftModalOpen, setShiftModalOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [queueStatus, setQueueStatus] = useState<'queued' | null>(null);
  const [queueBusy, setQueueBusy] = useState(false);
  const [queueItems, setQueueItems] = useState<OilQueueItem[]>([]);
  // Замены масла вне очереди — список из /queue (rev. 2026-09-11, схема §3 №9)
  const [notQueuedItems, setNotQueuedItems] = useState<OilQueueItem[]>([]);
  // «Провести и оплатить» — тестовое окно (OilChangePage.md №62)
  const [postingAvailable, setPostingAvailable] = useState(false);
  // «Пробить чек» — master + флаг receipt (direct_posting.md §7 rev. №8)
  const [fiscalizeAvailable, setFiscalizeAvailable] = useState(false);
  const [postPayOpen, setPostPayOpen] = useState(false);
  // Настройки вида — пункт «Настройки» в меню «⋯» (OilChangePage.md §3 rev. №59)
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Тумблер «Очередь масло снизу вверх» (localStorage, default OFF)
  const [queueBottomUp, setQueueBottomUp] = useState(() => localStorage.getItem('oilQueueBottomUp') === '1');
  const [printAvailable, setPrintAvailable] = useState(false);
  const [invoiceAvailable, setInvoiceAvailable] = useState(false);

  useEffect(() => {
    if (isNew) return;
    getOrderPostingAvailable().then(setPostingAvailable);
    getReceiptFiscalizeAvailable().then(setFiscalizeAvailable);
    getOrderPrintAvailable().then(setPrintAvailable);
    getTaxInvoiceAvailable().then(setInvoiceAvailable);
  }, [isNew]);

  // Исполнители (§3Г): единое поле вида; managedExecutorIds — «управляемое
  // множество» (что поле показывало при открытии, §3Г п.4 rev. №5)
  const [executorIds, setExecutorIds] = useState<string[]>([]);
  const [execOpen, setExecOpen] = useState(false);
  const [managedExecutorIds, setManagedExecutorIds] = useState<string[]>([]);
  // Модальное окно «Исполнитель не указан» с полем исполнителей (§3Г п.8, rev. №41)
  const [execModalOpen, setExecModalOpen] = useState(false);
  const [execModalIds, setExecModalIds] = useState<string[]>([]);
  // Блокировка формы: у строк услуг разные наборы исполнителей (§3Г п.9, rev. №6)
  const [executorMismatch, setExecutorMismatch] = useState(false);
  const [personOptions, setPersonOptions] = useState<{ value: string; label: string }[]>([]);
  const [pickedExecNames, setPickedExecNames] = useState<Record<string, string>>({});

  const [phoneInput, setPhoneInput] = useState('');
  const [clientCaption, setClientCaption] = useState('');
  const [productSearch, setProductSearch] = useState('');
  // Исполнители, предзаполненные автоматически (последняя заявка дня, §3Г п.2) —
  // базовая линия для признака pristine (§3Д rev. №43)
  const prefilledExecRef = useRef<string[]>([]);
  // GUID авто-услуг 1С (платная/бесплатная), резолвятся при открытии новой заявки
  const [autoGuids, setAutoGuids] = useState<{ paid: string | null; free: string | null }>({ paid: null, free: null });
  // Явный фокус на «Автомобиль» при переходе в новую заявку (rev. №55):
  // autoFocus срабатывает только при первом монтировании страницы
  const vehiclePickerRef = useRef<VehiclePickerRef>(null);

  // Новая заявка: авто-услуга «Масло в ДВС - замена» — реальная первая строка
  // черновика (OilChangePage.md §1 п.5); при выборе масла переключится на бесплатную
  const [draft, setDraft] = useState<Draft>(() => {
    const d = emptyDraft();
    d.services = [
      { ...newLine(), name: SERVICE_NAME_PAID, price: SERVICE_PRICE_DEFAULT },
      newLine(),
    ];
    return d;
  });

  const {
    serviceOptions, productOptions, warehouseOptions,
    loadServices, loadProducts, loadWarehouse,
    debouncedLoadServices, debouncedLoadProducts,
    findServiceGuid,
  } = useOneCCatalogs();

  const posted = order?.posted ?? false;
  // Помеченный на удаление / удалённый заказ — read-only (service_order_deletion.md, №60)
  const deletionPending = !!order?.deletion_state;
  const readOnly = posted || deletionPending;
  // Блокировка формы (§3Г п.9): разные наборы исполнителей по строкам;
  // новые заявки и проведённые (read-only) не блокируем
  const blocked = executorMismatch && !isNew && !posted;

  // Блеклость нетронутой новой заявки (§3Д rev. №43): признак вычисляемый —
  // возврат всего в исходное состояние включает блеклость обратно
  const pristine = useMemo(() => {
    if (!isNew) return false;
    const s = (v: string | null | undefined) => (v || '').trim();
    const d = draft;
    if (s(d.vehicle) || s(d.vehicle_make) || s(d.vehicle_model) ||
        s(d.license_plate) || s(d.vin) || d.mileage != null || d.year != null || s(d.color)) return false;
    if (d.client_id != null || s(d.client_label) || s(d.retail_buyer_name) ||
        s(d.notes) || d.prepayment_sum != null) return false;
    if (s(phoneInput) || s(clientCaption)) return false;
    if (d.parts.some(l => !isEmptyLine(l))) return false;
    if (d.services.some(l => !isEmptyLine(l) && !isAutoServiceName(l.name))) return false;
    const pre = prefilledExecRef.current;
    if (executorIds.length !== pre.length || !executorIds.every(x => pre.includes(x))) return false;
    return true;
  }, [isNew, draft, phoneInput, clientCaption, executorIds]);
  const dimPristine: React.CSSProperties = { opacity: pristine ? 0.45 : 1, transition: 'opacity 0.3s' };

  // ── Автодобавление сканов из буфера — наследие заказ-наряда (§3 rev. №16) ──
  // Тот же scanTargetStore, что у ServiceOrderPage: страница регистрирует handler,
  // при размонтировании — очищает (автодобавление только в реально открытый заказ)
  const draftRef = useRef(draft);
  useEffect(() => { draftRef.current = draft; }, [draft]);
  useEffect(() => {
    document.addEventListener('keydown', enterAsTab);
    document.addEventListener('keydown', arrowFieldNav, true);
    return () => {
      document.removeEventListener('keydown', enterAsTab);
      document.removeEventListener('keydown', arrowFieldNav, true);
    };
  }, []);
  const setScanTarget = useScanTargetStore(s => s.setTarget);
  const clearScanTarget = useScanTargetStore(s => s.clearTarget);
  const setScanTargetMarkingCodes = useScanTargetStore(s => s.setMarkingCodes);

  // Марки буфера сканирования — подсветка «марка в буфере» оранжевым текстом
  // и штатное удаление крестом (rev. 2026-09-13 №24)
  const bufferItems = useScanBufferStore(s => s.items);
  const bufferMarkingCodes = useMemo(
    () => new Set(bufferItems.map(s => s.marking_code).filter(Boolean) as string[]),
    [bufferItems],
  );

  // Подсветка «уже в этом заказе» в буфере — по маркам черновика (масло + запчасти)
  useEffect(() => {
    const codes = new Set<string>();
    draft.parts.forEach(l => {
      if (l.marking_code) codes.add(l.marking_code);
      (l.children || []).forEach(c => { if (c.marking_code) codes.add(c.marking_code); });
    });
    setScanTargetMarkingCodes(codes);
  }, [draft.parts, setScanTargetMarkingCodes]);

  useEffect(() => {
    // rev. 2026-09-18 №38: регистрируем и НОВУЮ (несохранённую) заявку —
    // orderId тогда null: checkCanAdd/resolveCode пропускаются, марка уходит
    // сразу в черновик, resolveCode выполняется после первого сохранения (doSave)
    if (readOnly || blocked) return;
    if (!order && !isNew) return;
    const orderId = order?.id ?? null;
    const orderNumber = order?.number ?? null;
    const orderLabel = orderNumber ? `ЗН № ${orderNumber}` : 'новую заявку';

    const handler = async (scan: BufferCode) => {
      // Единое правило продажи (marking_buffer §3, rev. 2026-10-23): только ready;
      // разлив из буфера не добавляется, непродаваемые статусы — отказ с причиной
      const notSellable = sellabilityError(scan);
      if (notSellable) {
        return { ok: false, message: notSellable };
      }

      // Дубль по marking_code (та же бутылка уже в заказе — масло или запчасти)
      const scanMarkingCode = scan.marking_code;
      if (scanMarkingCode) {
        const existingLine = draftRef.current.parts.find(
          l => !isEmptyLine(l) && (
            l.marking_code === scanMarkingCode ||
            (l.children || []).some(c => c.marking_code === scanMarkingCode)
          )
        );
        if (existingLine) {
          return {
            ok: false,
            message: `Эта бутылка уже добавлена в ${orderLabel} (${existingLine.name})`,
            duplicate: true,
          };
        }
      }

      const markStatus0 = await getOneCMarkStatus(scan.marking_code || scan.raw_code);
      // Режим «без оприходования» (unreceived_sales.md §1А): марки нет в ШУТ 1С
      // (сбой ЭДО) — товар резолвит backend (марка или связка по фасовке),
      // позиция помечается unreceived_pending, split при сохранении исключит её
      let markStatus = markStatus0;
      let unreceivedDraft = false;
      if (!markStatus.found || !markStatus.product_id) {
        try {
          const { data: rd } = await unreceivedApi.resolveForDraft(scan.marking_code);
          markStatus = {
            found: true,
            mark_id: null,
            code: scan.marking_code,
            product_id: rd.product_id,
            product_name: rd.product_name || scan.product_name || undefined,
          };
          unreceivedDraft = true;
        } catch {
          // режим OFF или товар неизвестен — отказ ниже
        }
      }
      if (!markStatus.found || !markStatus.product_id) {
        return { ok: false, message: 'Марка не найдена в справочнике 1С' };
      }

      let price = 0;
      let priceFromPriceList = false;
      try {
        const catalogResult = await getCatalogById(markStatus.product_id);
        // /1c/catalog возвращает плоский массив (fix 2026-09-11: items?.[0] всегда
        // давал undefined → цена 0 у марок, добавленных сканом)
        const product = catalogResult[0];
        price = product?.price ?? 0;
        priceFromPriceList = !!product?.price_from_price_list;
      } catch {
        // Цену получить не удалось — добавляем с 0, пользователь поправит
      }

      const newPart: DraftLine = {
        key: Math.random().toString(36).slice(2),
        external_1c_id: markStatus.product_id,
        name: markStatus.product_name || 'Маркированный товар',
        quantity: 1,
        unit: 'шт',
        price: Number(price),
        price_from_price_list: priceFromPriceList || undefined,
        marking_code: scan.marking_code,
        unreceived_pending: unreceivedDraft || undefined,
      };
      const isDuplicate = draftRef.current.parts.some(
        l => !isEmptyLine(l) && l.external_1c_id === newPart.external_1c_id
      );

      setDirty(true);
      setDraft(d => {
        // Штучная марка — в мастер-группу товара (1 марка = 1 шт). Моторное масло
        // попадёт в секцию «Масло» автоматически (oilLines — производная draft.parts)
        const parts = d.parts.filter(l => !isEmptyLine(l));
        const masterIdx = parts.findIndex(
          l => l.is_piece_master && l.external_1c_id === newPart.external_1c_id
        );
        if (masterIdx >= 0) {
          const m = parts[masterIdx];
          const child: DraftLine = {
            ...newPart,
            key: `${m.key}:${scan.marking_code}`,
            parent_key: m.key,
            quantity: 1,
          };
          parts[masterIdx] = {
            ...m,
            children: [...(m.children || []), child],
            quantity: (m.children?.length || 0) + 1,
          };
        } else {
          parts.push({
            ...newPart,
            marking_code: null,
            is_piece_master: true,
            children: [{
              ...newPart,
              key: `${newPart.key}:${scan.marking_code}`,
              parent_key: newPart.key,
              quantity: 1,
            }],
          });
        }
        return { ...d, parts: ensureTrailingEmpty(parts) };
      });

      return { ok: true, message: `${newPart.name} добавлен в ${orderLabel} (не забудьте сохранить)`, key: newPart.key, duplicate: isDuplicate };
    };

    const barcodeHandler = (product: BarcodeProduct) => {
      const result = addBarcodeProductToLines(draftRef.current.parts, product);
      if (result.ok) {
        setDirty(true);
        setDraft(d => ({ ...d, parts: result.lines }));
        if (result.bulkProductId && result.key) {
          void loadBulkMarks(result.key, result.bulkProductId, orderId ?? undefined);
        }
      }
      return result;
    };

    setScanTarget(`В ${orderLabel}`, handler, orderId, orderNumber, barcodeHandler);
    return () => clearScanTarget();
  }, [isNew, order, readOnly, blocked, setScanTarget, clearScanTarget]);

  // ── Очередь (колонка слева, OilChangePage.md §3 rev. 2026-09-08) ─────────
  const loadQueue = useCallback(async () => {
    try {
      const q = await getQueue();
      setQueueItems(q.items);
      setNotQueuedItems(q.not_queued || []);
    } catch { /* очередь необязательна для формы */ }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const queuePosition = useMemo(() => {
    if (queueStatus !== 'queued' || !id) return null;
    return queueItems.find(i => i.order_id === Number(id))?.position ?? null;
  }, [queueItems, queueStatus, id]);

  // Отображаемый порядок списков (rev. №59): «снизу вверх» переворачивает оба списка,
  // порядок блоков («Очередь» выше «Не в очереди») и карточки «+» не меняются
  const displayQueueItems = useMemo(
    () => (queueBottomUp ? [...queueItems].reverse() : queueItems),
    [queueItems, queueBottomUp],
  );
  const displayNotQueuedItems = useMemo(
    () => (queueBottomUp ? [...notQueuedItems].reverse() : notQueuedItems),
    [notQueuedItems, queueBottomUp],
  );

  const handleQueueBottomUpChange = (checked: boolean) => {
    setQueueBottomUp(checked);
    localStorage.setItem('oilQueueBottomUp', checked ? '1' : '0');
  };

  // ── Исполнители (§3Г, rev. 2026-09-10 №2) ────────────────────────────────
  // Опции — физлица 1С (/1c/persons), как в полном виде ЗН; закреплённые первыми
  const loadPersons = useCallback(async (q?: string) => {
    try {
      const r = await api.get('/1c/persons', { params: { search: q } });
      const items = Array.isArray(r.data) ? r.data : (r.data.persons || []);
      const fresh = items.map((p: any) => ({ value: p.id, label: p.name }));
      setPersonOptions(prev => {
        const map = new Map(prev.map(o => [o.value, o]));
        fresh.forEach((o: { value: string; label: string }) => map.set(o.value, o));
        return Array.from(map.values());
      });
    } catch { /* исполнители необязательны */ }
  }, []);

  useEffect(() => {
    loadPersons();
    PINNED_EXECUTOR_SURNAMES.forEach(s => loadPersons(s));
  }, [loadPersons]);

  // Имена исполнителей: опции + исполнители строк услуг текущего заказа
  // (чтобы теги Select показывали имена, а не сырые GUID)
  const personNameMap = useMemo(() => {
    const map = new Map(personOptions.map(o => [o.value, o.label]));
    Object.entries(pickedExecNames).forEach(([eid, name]) => { if (!map.has(eid)) map.set(eid, name); });
    draft.services.forEach(l => {
      ([[l.executor1_id, l.executor1_name],
        [l.executor2_id, l.executor2_name],
        [l.executor3_id, l.executor3_name]] as const).forEach(([eid, name]) => {
        if (eid && !map.has(eid)) map.set(eid, name || eid);
      });
    });
    return map;
  }, [personOptions, pickedExecNames, draft.services]);

  const executorOptions = useMemo(() =>
    Array.from(personNameMap.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => pinnedExecutorRank(a.label) - pinnedExecutorRank(b.label)),
  [personNameMap]);

  // Новая заявка: предзаполнение исполнителями последней сегодняшней замены масла
  // (новый день / в последней заявке пусто → поле пустое)
  const prefillExecutors = useCallback(() => {
    getLastExecutors()
      .then(list => {
        const ids = list.map(e => e.id).slice(0, 3);
        prefilledExecRef.current = ids;
        setExecutorIds(ids);
        setPersonOptions(prev => {
          const map = new Map(prev.map(o => [o.value, o]));
          list.forEach(e => { if (!map.has(e.id)) map.set(e.id, { value: e.id, label: e.name }); });
          return Array.from(map.values());
        });
      })
      .catch(() => { /* поле останется пустым */ });
  }, []);

  // ── Разлив: обёртки над общей логикой (components/service-order/bulk.ts) ──
  const loadBulkMarks = useCallback(async (key: string, productId: string, serviceOrderId?: number) => {
    try {
      const marks = await fetchProductMarks(productId, serviceOrderId);
      setDraft(d => ({ ...d, parts: ensureTrailingEmpty(applyMarksToMaster(d.parts, key, marks)) }));
    } catch {
      message.error('Не удалось загрузить марки товара');
    }
  }, []);

  // rev. 2026-09-30 (№48): плоская литровая строка без марки (мастер разлива,
  // сохранённый без марок как предварительный документ) восстанавливается мастером —
  // иначе авто-распределение не работало и объём уходил в 1С без марки (красная в чеке)
  const restoreBulkMasters = useCallback(async (parts: DraftLine[], serviceOrderId?: number) => {
    for (const l of findRestorableBulkLines(parts)) {
      try {
        const marks = await fetchOwnProductMarks(l.external_1c_id!, serviceOrderId);
        if (marks.length > 0) {
          setDraft(d => ({ ...d, parts: ensureTrailingEmpty(promoteToBulkMaster(d.parts, l.key, marks)) }));
        }
      } catch { /* тихо: строка остаётся плоской */ }
    }
  }, []);

  // ── Загрузка существующего заказа ─────────────────────────────────────────
  useEffect(() => {
    if (isNew) return;
    getOrder(Number(id))
      .then(o => {
        // Вид замены масла — только для розницы (OilChangePage.md §1 п.7, rev. 2026-09-08):
        // заказ с юрлицом открываем в полном виде
        if (o.client_type === 'counterparty') {
          message.info('Заказ с юрлицом открывается в полном виде заказ-наряда');
          navigate(`/service-orders/${id}`, { replace: true });
          return;
        }
        setOrder(o);
        setQueueStatus(o.queue_status ?? null);
        setClientCaption(o.counterparty || o.retail_buyer_name || '');
        const d = orderToDraft(o);
        setDraft(d);
        // Поле «Исполнители» = объединение исполнителей всех строк услуг (§3Г п.4);
        // это же «управляемое множество» для сохранения
        const execIds = unionServiceExecutors(d.services);
        setExecutorIds(execIds.slice(0, 3));
        setManagedExecutorIds(execIds.slice(0, 3));
        setExecutorMismatch(hasMixedServiceExecutors(d.services));
        setLoading(false);
        // Потолки объёма разливных мастер-строк — догрузка марок (иначе схлопнутся)
        d.parts.forEach(l => {
          if (l.is_bulk_master && l.external_1c_id) loadBulkMarks(l.key, l.external_1c_id, o.id);
        });
        // Восстанавливаем мастер разлива у литровых строк, сохранённых без марок (№48)
        restoreBulkMasters(d.parts, o.id);
      })
      .catch(() => {
        message.error('Не удалось загрузить заказ');
        setLoading(false);
      });
  }, [id, isNew, navigate, loadBulkMarks, restoreBulkMasters]);

  // ── Сброс в чистую новую заявку (§3 rev. №13, §3Д rev. №44): переход
  // :id → /oil-change НЕ перемонтирует компонент, а /oil-change → /oil-change
  // не меняет isNew — поэтому сброс вызывается и эффектом, и явно из handleNewOrder
  const resetNewOrder = useCallback(() => {
    setOrder(null);
    setQueueStatus(null);
    setLoading(false);
    setDirty(false);
    setPhoneInput('');
    setClientCaption('');
    setProductSearch('');
    setExecutorIds([]);
    prefilledExecRef.current = [];
    setManagedExecutorIds([]);
    setExecutorMismatch(false);
    const d = emptyDraft();
    d.services = [
      { ...newLine(), name: SERVICE_NAME_PAID, price: SERVICE_PRICE_DEFAULT, external_1c_id: autoGuids.paid },
      newLine(),
    ];
    setDraft(d);
    prefillExecutors();
    setTimeout(() => vehiclePickerRef.current?.focus(), 0);
  }, [autoGuids.paid, prefillExecutors]);

  useEffect(() => {
    if (!isNew) return;
    resetNewOrder();
  }, [isNew]);

  // ── Справочники ───────────────────────────────────────────────────────────
  useEffect(() => {
    loadWarehouse();
    loadServices();
  }, [loadWarehouse, loadServices]);

  // Авто-услуги новой заявки: резолвим GUID обеих услуг 1С (платная/бесплатная),
  // чтобы в 1С строка уходила с наименованием (§1 п.5)
  useEffect(() => {
    if (!isNew) return;
    Promise.all([findServiceGuid(SERVICE_NAME_PAID), findServiceGuid(SERVICE_NAME_FREE)])
      .then(([paid, free]) => setAutoGuids({ paid: paid?.id ?? null, free: free?.id ?? null }));
  }, [isNew, findServiceGuid]);

  // ── Моторное масло: какие строки запчастей — масло ────────────────────────
  const oilOptionIds = useMemo(
    () => new Set(warehouseOptions.filter(isMotorOilOption).map(o => o.value)),
    [warehouseOptions],
  );
  const isOilLine = useCallback((l: DraftLine): boolean =>
    !isEmptyLine(l) && (!!l.external_1c_id && oilOptionIds.has(l.external_1c_id) || isMotorOilName(l.name)),
  [oilOptionIds]);

  const oilLines = useMemo(() => draft.parts.filter(isOilLine), [draft.parts, isOilLine]);
  const partLines = useMemo(() => draft.parts.filter(l => !isOilLine(l)), [draft.parts, isOilLine]);

  // Масло-селектор (OilChangePage.md §1 п.8): сначала разлив, затем штучные
  // немаркированные; маркированные штучные скрыты барьером ещё в хуке
  const oilSelectOptions = useMemo(() => {
    const oils = filterWarehouseOptions(warehouseOptions, productSearch).filter(isMotorOilOption);
    const map = new Map<string, CatalogOption>(oils.map(o => [o.value, o]));
    // Масло текущего заказа — в опциях, даже если склада ещё нет
    oilLines.forEach(l => {
      if (l.external_1c_id && !map.has(l.external_1c_id)) {
        map.set(l.external_1c_id, {
          value: l.external_1c_id, label: l.name || l.external_1c_id,
          name: l.name, price: l.price, unit: l.unit || 'шт',
        });
      }
    });
    const all = Array.from(map.values());
    const byAvail = (a: CatalogOption, b: CatalogOption) => (b.available ?? 0) - (a.available ?? 0);
    // Разлив: сначала Mobil, затем ZIC, потом остальные бренды (внутри — по остатку)
    const BRAND_ORDER = ['mobil', 'zic'];
    const brandRank = (o: CatalogOption) => {
      const i = BRAND_ORDER.indexOf((o.brand || '').trim().toLowerCase());
      return i === -1 ? BRAND_ORDER.length : i;
    };
    const byBrandThenAvail = (a: CatalogOption, b: CatalogOption) =>
      brandRank(a) - brandRank(b) || byAvail(a, b);
    return [
      { label: 'На разлив', options: redLast(all.filter(o => isVolumeUnit(o.unit))).sort(byBrandThenAvail) },
      { label: 'Фасовка', options: redLast(all.filter(o => !isVolumeUnit(o.unit))).sort(byAvail) },
    ];
  }, [warehouseOptions, oilLines, productSearch]);

  // ── Правая колонка «В фасовке» (§3Б): штучные фасовки выбранного разливного масла ──
  const [packaged, setPackaged] = useState<PackagedInfo | null>(null);
  const bulkOilMaster = useMemo(
    () => oilLines.find(l => l.is_bulk_master && l.external_1c_id) ?? null,
    [oilLines],
  );
  const bulkOilCatalogId = useMemo(() => {
    if (!bulkOilMaster?.external_1c_id) return null;
    return warehouseOptions.find(o => o.value === bulkOilMaster.external_1c_id)?.catalogId ?? null;
  }, [bulkOilMaster, warehouseOptions]);
  useEffect(() => {
    if (!bulkOilCatalogId) { setPackaged(null); return; }
    let cancelled = false;
    api.get(`/price-list/packaged/${bulkOilCatalogId}`)
      .then(r => { if (!cancelled) setPackaged(r.data?.packages?.length ? r.data : null); })
      .catch(() => { if (!cancelled) setPackaged(null); });
    return () => { cancelled = true; };
  }, [bulkOilCatalogId]);

  const oilVolume = bulkOilMaster?.quantity ?? 0;

  // Комбинация упаковок под объём заявки: от фасовки строки вниз, жадно,
  // последний добор — округление вверх (§3Б)
  const buildCombo = (rowVolume: number): string => {
    if (!packaged) return '';
    const pkgs = packaged.packages;
    const desc = pkgs.map(p => p.volume).sort((a, b) => b - a);
    const seq: number[] = [rowVolume];
    let remaining = oilVolume - rowVolume;
    while (remaining > 1e-9) {
      const fit = desc.find(v => v <= remaining + 1e-9);
      const v = fit ?? desc[desc.length - 1];
      seq.push(v);
      remaining -= v;
    }
    const counts = new Map<number, number>();
    seq.forEach(v => counts.set(v, (counts.get(v) ?? 0) + 1));
    const priceOf = (v: number) => pkgs.find(p => p.volume === v)?.price ?? 0;
    const parts = Array.from(counts.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([v, c]) => (c >= 3 ? `${fmtVol(v)}л × ${c}` : Array(c).fill(`${fmtVol(v)}л`).join(' + ')));
    const total = seq.reduce((s, v) => s + priceOf(v), 0);
    return `${parts.join(' + ')} = ${total.toLocaleString('ru-RU')} ₽`;
  };

  // Правая колонка постоянная (§3Б rev. №10): сверху «Итоги», ниже «В фасовке»
  const renderRightColumn = () => (
    <div style={{ width: 260, flexShrink: 0, ...dimPristine }}>
      {/* Итоги (перенесены из нижней части формы); без заливки — только обводка (rev. 2026-09-11 №3) */}
      <div style={{ padding: 12, borderRadius: 8, border: '1px solid #b7eb8f' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          {oilLines.length === 0 && isNew && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">Масло клиента</Text>
              <Text type="secondary">—</Text>
            </div>
          )}
          {draft.parts.filter(l => !isEmptyLine(l)).map(l => (
            <div key={l.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <Text style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</Text>
              <Text style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{lineTotal(l).toLocaleString('ru-RU')} ₽</Text>
            </div>
          ))}
          {draft.services.filter(l => !isEmptyLine(l)).map(l => (
            <div key={l.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <Text style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</Text>
              <Text style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{lineTotal(l).toLocaleString('ru-RU')} ₽</Text>
            </div>
          ))}
          <Divider style={{ margin: '8px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text strong style={{ fontSize: 16 }}>Итого</Text>
            <Text strong style={{ fontSize: 16 }}>{total.toLocaleString('ru-RU')} ₽</Text>
          </div>
          {unreceivedSum > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text style={{ color: '#fa8c16', fontSize: 12 }}>Из них ожидает оприходования:</Text>
              <Text style={{ color: '#fa8c16', fontSize: 12 }}>{unreceivedSum.toLocaleString('ru-RU')} ₽</Text>
            </div>
          )}
        </Space>
      </div>

      {/* «В фасовке» — только при выбранном разливном масле с фасовками */}
      {packaged && (
        <div style={{ marginTop: 16 }}>
          <Text strong style={{ fontSize: 12 }}>В фасовке</Text>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>{packaged.name}</Text>
          </div>
          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f0f0f0', paddingBottom: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Фасовка</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>Цена за шт</Text>
            </div>
            {packaged.packages.map(p => (
              <Tooltip
                key={p.volume}
                placement="left"
                title={oilVolume > 0 ? buildCombo(p.volume) : 'Укажите объём масла в заявке'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #fafafa' }}>
                  <Text>{fmtVol(p.volume)} л</Text>
                  <Text>{p.price.toLocaleString('ru-RU')} ₽</Text>
                </div>
              </Tooltip>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Авто-услуга в новой заявке (§1 п.5): масло выбрано → бесплатная замена 0 ₽,
  // иначе платная 1200 ₽ (rev. 2026-09-26). Переключаем имя + GUID + цену.
  useEffect(() => {
    if (!isNew) return;
    const hasOil = oilLines.length > 0;
    setDraft(d => ({
      ...d,
      services: d.services.map(l => {
        if (!isAutoServiceName(l.name)) return l;
        return {
          ...l,
          name: hasOil ? SERVICE_NAME_FREE : SERVICE_NAME_PAID,
          price: hasOil ? 0 : SERVICE_PRICE_DEFAULT,
          external_1c_id: hasOil ? autoGuids.free : autoGuids.paid,
        };
      }),
    }));
  }, [isNew, oilLines.length, autoGuids]);

  // ── Правки черновика (общие обёртки, как в ServiceOrderPage) ─────────────
  const updateLine = (type: 'service' | 'part', key: string, patch: Partial<DraftLine>) => {
    setDirty(true);
    setDraft(d => {
      const src = type === 'service' ? d.services : d.parts;
      const updated = src.map(l => l.key === key ? { ...l, ...patch, mark_status: null } : l);
      return { ...d, [type === 'service' ? 'services' : 'parts']: ensureTrailingEmpty(updated) };
    });
  };

  const removeLine = (type: 'service' | 'part', key: string) => {
    setDirty(true);
    setDraft(d => {
      const src = type === 'service' ? d.services : d.parts;
      const line = src.find(l => l.key === key);
      if (order?.id) {
        // Мгновенная отвязка марок при удалении строки сохранённого ЗН
        if (line?.marking_code) markingApi.unbindCode(line.marking_code).catch(() => {});
        line?.children?.forEach(c => {
          if (c.marking_code) markingApi.unbindCode(c.marking_code).catch(() => {});
        });
      }
      return {
        ...d,
        [type === 'service' ? 'services' : 'parts']: ensureTrailingEmpty(src.filter(l => l.key !== key)),
      };
    });
  };

  // Удаление штучной марки из piece-мастера (rev. 2026-09-13 №24): штатно — крестом
  // (марка в буфере) или аварийно из модалки DataMatrix; мастер без марок удаляется
  const removePieceMark = (masterKey: string, childKey: string) => {
    setDirty(true);
    setDraft(d => {
      const master = d.parts.find(l => l.key === masterKey);
      const child = master?.children?.find(c => c.key === childKey);
      if (child?.marking_code && order?.id) {
        markingApi.unbindCode(child.marking_code).catch(() => {});
      }
      const updated = d.parts
        .map(l => {
          if (l.key !== masterKey) return l;
          const children = (l.children || []).filter(c => c.key !== childKey);
          return { ...l, children, quantity: children.length, mark_status: null };
        })
        .filter(l => l.key !== masterKey || (l.children || []).length > 0);
      return { ...d, parts: ensureTrailingEmpty(updated) };
    });
  };

  const changeMasterQty = (masterKey: string, newQty: number) => {
    setDirty(true);
    setDraft(d => ({
      ...d,
      parts: ensureTrailingEmpty(d.parts.map(l =>
        l.key === masterKey ? { ...redistributeMaster(l, newQty), mark_status: null } : l)),
    }));
  };

  // (rev. 2026-09-13 №26) По-марочная правка разлива (updateChildVolume,
  // toggleChildManual, removeChildLine, addManualChild + MarkPickerModal) из этого
  // вида убрана — выбор/правка/удаление марок разлива только в полном виде ЗН.

  // ── Подбор позиций ────────────────────────────────────────────────────────
  const pickPart = (key: string, val: string | null) => {
    if (val == null) { updateLine('part', key, { external_1c_id: null }); return; }
    // Группа «Наш склад» в приоритете — её опции несут те же поля (name = имя 1С)
    const opt = warehouseOptions.find(o => o.value === val) || productOptions.find(o => o.value === val);
    if (!opt) return;
    const volume = isVolumeUnit(opt.unit);
    updateLine('part', key, {
      external_1c_id: opt.value,
      name: opt.name,
      unit: opt.unit || 'шт',
      price: opt.price ?? 0,
      recommended_price: opt.price ?? 0,
      quantity: volume ? 0 : 1,
      marking_code: null,
      price_from_price_list: opt.priceFromPriceList || undefined,
      is_bulk_master: volume || undefined,
      children: volume ? [] : undefined,
      available_marks: null,
    });
    if (volume) loadBulkMarks(key, opt.value, order?.id);
  };

  const pickService = (key: string, val: string | null) => {
    if (val == null) {
      updateLine('service', key, { external_1c_id: null, name: '', price: 0, unit: 'шт' });
      return;
    }
    const opt = mergedServiceOptions.find(o => o.value === val);
    if (!opt) return;
    updateLine('service', key, { external_1c_id: opt.value, name: opt.name, unit: opt.unit || 'шт', price: opt.price || 0 });
  };

  // Выбор масла (rev. №32): пустая хвостовая строка — добавление, заполненная —
  // замена только своей строки, «Убрать» — удаление только своей строки
  const handleOilChange = (val: string | undefined, targetKey: string) => {
    setDirty(true);
    const target = draft.parts.find(l => l.key === targetKey);
    // Отвязываем марки заменяемой/удаляемой строки у сохранённого заказа
    if (order?.id && target) {
      if (target.marking_code) markingApi.unbindCode(target.marking_code).catch(() => {});
      target.children?.forEach(c => { if (c.marking_code) markingApi.unbindCode(c.marking_code).catch(() => {}); });
    }
    if (val == null) {
      // Убрать строку масла (плейсхолдер не входит в draft.parts — для него ничего не делаем)
      if (target) {
        setDraft(d => ({ ...d, parts: ensureTrailingEmpty(d.parts.filter(l => l.key !== targetKey)) }));
      }
      return;
    }
    const opt = oilSelectOptions.flatMap(g => g.options).find(o => o.value === val);
    if (!opt) return;
    const volume = isVolumeUnit(opt.unit);
    const line: DraftLine = {
      ...newLine(),
      external_1c_id: opt.value,
      name: opt.name,
      unit: opt.unit || 'шт',
      price: opt.price ?? 0,
      recommended_price: opt.price ?? 0,
      quantity: volume ? 0 : 1,
      price_from_price_list: opt.priceFromPriceList || undefined,
      is_bulk_master: volume || undefined,
      children: volume ? [] : undefined,
      available_marks: null,
    };
    if (target) {
      // Замена конкретной строки — сохраняем её позицию в списке
      setDraft(d => ({
        ...d,
        parts: ensureTrailingEmpty(d.parts.map(l => (l.key === targetKey ? line : l))),
      }));
    } else {
      // Добавление из плейсхолдера — новая строка первой среди масляных
      setDraft(d => {
        const idx = d.parts.findIndex(isOilLine);
        const parts = idx === -1
          ? [line, ...d.parts]
          : [...d.parts.slice(0, idx), line, ...d.parts.slice(idx)];
        return { ...d, parts: ensureTrailingEmpty(parts) };
      });
    }
    if (volume) loadBulkMarks(line.key, opt.value, order?.id);
    window.setTimeout(() => {
      const el = document.getElementById(`qty-${line.key}`) as HTMLInputElement | null;
      if (el) { el.focus(); el.select(); }
    }, 0);
  };

  // ── Сохранение (только по кнопке — OilChangePage.md §2) ──────────────────
  // executorsOverride — исполнители, выбранные в модальном окне (rev. №41):
  // state ещё не успел обновиться к моменту сохранения
  // allowClosedShift — экстренный обход барьера «смена закрыта» (rev. №47):
  // true только из ShiftClosedModal по долгому удержанию кнопки
  // cardPaymentOverride — способ оплаты из окна «Провести и оплатить» (rev. №66):
  // выбор нал/карта в окне ≠ шапке заказа → заказ сохраняется с новым способом
  // до цепочки пробития, иначе шапка заказа и payment_form чека 1С врут
  const doSave = async (executorsOverride?: string[], allowClosedShift = false, cardPaymentOverride?: boolean): Promise<boolean> => {
    // Валидация разливных мастер-строк: объём должен быть покрыт марками
    const bad = bulkCoverageError(draft.parts, isEmptyLine);
    if (bad) {
      message.error(`«${bad.name}»: объём ${bad.quantity} ${bad.unit} не покрыт марками — недостаточно остатка`);
      return false;
    }
    setSaving(true);
    try {
      // Поле «Исполнители» (§3Г п.4, rev. №5) — «управляемое множество»:
      // снимаются только исполнители, которые поле показывало при открытии;
      // сторонние по-строчные исполнители сохраняются; пустое поле очищает
      const draftToSave: Draft = {
        ...draft,
        ...(cardPaymentOverride !== undefined ? { card_payment: cardPaymentOverride } : {}),
        services: applyOilChangeExecutors(
          draft.services, (executorsOverride ?? executorIds).slice(0, 3), managedExecutorIds,
          (eid) => personNameMap.get(eid) ?? null,
        ),
      };
      const payload = buildPayload(draftToSave);
      if (isNew) {
        const created = await createOrder({
          vehicle_id: payload.vehicle_id,
          vehicle_make: payload.vehicle_make,
          vehicle_model: payload.vehicle_model,
          license_plate: payload.license_plate,
          vin: payload.vin,
          manufacture_year: payload.manufacture_year,
          color: payload.color,
          client_type: payload.client_type,
          client_id: payload.client_id,
          retail_buyer_name: payload.retail_buyer_name,
          mileage: payload.mileage,
          recommendations: payload.recommendations,
          prepayment_sum: payload.prepayment_sum,
          work_start_date: payload.work_start_date,
          work_end_date: payload.work_end_date,
          items: payload.items,
          payment_type: payload.payment_type,
          card_payment: payload.card_payment,
          order_type: 'oil_change',
          enqueue: enqueueOnSave,
          allow_closed_shift: allowClosedShift,
        });
        message.success(enqueueOnSave ? 'Создано — заказ в очереди на пост' : 'Создано');
        if (created.warning) message.warning(created.warning, 8);
        // rev. 2026-09-18 №38: марки, добавленные сканом в новую заявку, оставались
        // в буфере (orderId был null) — резолвим их с id созданного заказа
        const draftMarkCodes = new Set<string>();
        draftToSave.parts.forEach(l => {
          if (!isEmptyLine(l) && l.marking_code) draftMarkCodes.add(l.marking_code);
          (l.children || []).forEach(c => { if (c.marking_code) draftMarkCodes.add(c.marking_code); });
        });
        for (const mc of Array.from(draftMarkCodes)) {
          const scan = bufferItems.find(s => s.marking_code === mc);
          if (!scan || scan.product_type === 'bulk') continue;
          try {
            await markingApi.resolveCode(mc, {
              action: 'sale',
              order_id: created.id,
              order_number: created.number,
            });
          } catch { /* марка уже не в буфере — не мешает сохранению */ }
        }
        useScanBufferStore.getState().refreshItems();
        setDirty(false);
        loadQueue();
        navigate(`/oil-change/${created.id}`, { replace: true });
        return true;
      } else {
        const updated = await updateOrder(Number(id), { ...payload, base_version: order?.content_version, allow_closed_shift: allowClosedShift });
        setOrder(updated);
        setQueueStatus(updated.queue_status ?? null);
        const d = orderToDraft(updated);
        setDraft(d);
        // «Управляемое множество» переводится на то, что реально ушло в заказ (§3Г п.4)
        const execIds = unionServiceExecutors(d.services);
        setExecutorIds(execIds.slice(0, 3));
        setManagedExecutorIds(execIds.slice(0, 3));
        // orderToDraft сбрасывает available_marks/available_volume (null) —
        // без догрузки потолки объёма схлопываются до текущих значений
        d.parts.forEach(l => {
          if (l.is_bulk_master && l.external_1c_id) loadBulkMarks(l.key, l.external_1c_id, updated.id);
        });
        restoreBulkMasters(d.parts, updated.id);
        setDirty(false);
        message.success('Сохранено');
        if (updated.warning) message.warning(updated.warning, 8);
        return true;
      }
    } catch (e: any) {
      // 409 shift_not_open (1c.md §1 rev. 21, rev. №47): кассовая смена закрыта —
      // окно с экстренным обходом по долгому удержанию кнопки
      if (isShiftClosedError(e)) {
        setShiftModalOpen(true);
      } else if (e?.response?.status === 409 && id) {
        // 409 — конкурентное сохранение (1c.md §3 rev. 19): показать, кто изменил,
        // и предложить осознанно загрузить свежую версию (свои правки теряются)
        Modal.warning({
          title: 'Заказ изменён другим пользователем',
          content: typeof e?.response?.data?.detail === 'string'
            ? e.response.data.detail
            : 'Пока вы редактировали, заказ был сохранён другим пользователем.',
          okText: 'Загрузить свежую версию',
          onOk: () => window.location.reload(),
        });
      } else {
        message.error(formatApiError(e));
      }
    } finally {
      setSaving(false);
    }
    return false;
  };

  // Сохранение без исполнителя — модальное окно с полем исполнителей (§3Г п.8, rev. №41);
  // проверка идёт ДО валидаций (они — внутри doSave)
  const handleSave = () => {
    if (executorIds.length === 0) {
      setExecModalIds([]);
      setExecModalOpen(true);
      return;
    }
    void doSave();
  };

  // ── Переключение вида (тумблер в «⋯») ─────────────────────────────────────
  const switchToServiceOrderView = async () => {
    // Новая несохранённая заявка: черновик в ЗН не переносится — предупреждаем
    // о потере данных (rev. №39, инцидент ЗН 0010137 — молчаливая потеря авто)
    if (isNew) {
      if (!dirty) { navigate('/service-orders/new'); return; }
      Modal.confirm({
        title: 'Перейти в заказ-наряд?',
        content: 'Заполненные данные не перенесутся в заказ-наряд и будут потеряны.',
        okText: 'Перейти', cancelText: 'Остаться',
        onOk: () => navigate('/service-orders/new'),
      });
      return;
    }
    const doSwitch = async () => {
      try {
        await setOrderType(Number(id), 'service_order');
      } catch { /* вид локальный — переключаем в любом случае */ }
      navigate(`/service-orders/${id}`);
    };
    if (dirty) {
      Modal.confirm({
        title: 'Есть несохранённые изменения',
        content: 'Переключить вид без сохранения? Изменения будут потеряны.',
        okText: 'Переключить', cancelText: 'Отмена',
        onOk: doSwitch,
      });
    } else {
      await doSwitch();
    }
  };

  // ── Очередь ───────────────────────────────────────────────────────────────
  const queueAction = async (fn: (id: number) => Promise<void>, okMsg: string, next: 'queued' | null) => {
    if (!id) return;
    setQueueBusy(true);
    try {
      await fn(Number(id));
      setQueueStatus(next);
      message.success(okMsg);
      loadQueue();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Ошибка очереди');
    } finally {
      setQueueBusy(false);
    }
  };

  // ── Телефон клиента ───────────────────────────────────────────────────────
  const handlePhoneBlur = async () => {
    let phone = phoneInput.replace(/\D/g, '');
    if (!phone) return;
    if (phone.startsWith('7') && phone.length === 11) phone = phone.slice(1);
    if (phone.length !== 10) return;
    try {
      const r = await api.get(`/individuals?phone=%2B7${phone}&per_page=1`);
      const items = r.data.items || [];
      let cid: number;
      let caption: string;
      if (items.length > 0) {
        cid = items[0].id;
        caption = [items[0].last_name, items[0].first_name].filter(Boolean).join(' ');
      } else {
        const cr = await api.post('/individuals', {
          last_name: '-', first_name: '-', phones: [`+7${phone}`],
        });
        cid = cr.data.id;
        caption = `+7${phone}`;
      }
      setClientCaption(caption);
      setDirty(true);
      setDraft(d => ({ ...d, client_type: 'individual', client_id: cid }));
    } catch { /* ignore */ }
  };

  // ── Селекторы опций ───────────────────────────────────────────────────────
  const mergedServiceOptions = useMemo(() => {
    const map = new Map<string, CatalogOption>(serviceOptions.map(o => [o.value, o]));
    draft.services.forEach(l => {
      if (l.external_1c_id && !map.has(l.external_1c_id)) {
        map.set(l.external_1c_id, {
          value: l.external_1c_id, label: l.name || l.external_1c_id,
          name: l.name, price: l.price, unit: l.unit || 'шт',
        });
      }
    });
    return Array.from(map.values());
  }, [serviceOptions, draft.services]);

  // Запчасти (OilChangePage.md §1 п.8): «Наш склад» без моторных масел + «Каталог 1С».
  // Барьеры маркировки — как в ЗН (маркированные штучные и жидкости скрыты на уровне запросов)
  const groupedPartOptions = useMemo(() => {
    const whIds = new Set(warehouseOptions.map(o => o.value));
    const wh = filterWarehouseOptions(warehouseOptions, productSearch)
      .filter(o => !isMotorOilOption(o));
    const map = new Map<string, CatalogOption>(productOptions.map(o => [o.value, o]));
    partLines.forEach(l => {
      const add = (x: DraftLine) => {
        if (x.external_1c_id && !map.has(x.external_1c_id) && !whIds.has(x.external_1c_id)) {
          map.set(x.external_1c_id, {
            value: x.external_1c_id, label: x.name || x.external_1c_id,
            name: x.name, price: x.price, unit: x.unit || 'шт',
          });
        }
      };
      add(l);
      (l.children || []).forEach(add);
    });
    const cat = Array.from(map.values()).filter(o => !whIds.has(o.value));
    // «Фильтры первыми» (масляный → воздушный → салонный → топливный), красные в конце (№29)
    return [
      { label: 'Наш склад', options: filtersFirst(wh) },
      { label: 'Каталог 1С', options: filtersFirst(cat) },
    ];
  }, [warehouseOptions, productOptions, productSearch, partLines]);

  // Плейсхолдер пустой строки масла. Хук — ВЫШЕ early return по loading,
  // иначе React #310 при перезагрузке страницы /oil-change/:id
  const oilPlaceholder = useMemo(() => newLine(), []);

  if (loading) return <Spin style={{ display: 'block', margin: '40px auto' }} />;

  // ── Итоги (строго по реальным позициям, §1 п.5) ───────────────────────────
  const lineTotal = (l: DraftLine): number => l.quantity * l.price;
  const partsTotal = draft.parts.filter(l => !isEmptyLine(l)).reduce((s, l) => s + lineTotal(l), 0);
  // Отложенные позиции режима «без оприходования» (unreceived_sales.md v3)
  const unreceivedSum = draft.parts
    .filter(l => l.unreceived_pending && !l.parent_key && !isInfoRow(l))
    .reduce((sum, l) => sum + l.quantity * l.price, 0);
  const servicesTotal = draft.services.filter(l => !isEmptyLine(l)).reduce((s, l) => s + lineTotal(l), 0);
  const total = partsTotal + servicesTotal;
  // Сумма услуг отдельно (строка «Итого» под таблицей услуг, rev. 2026-09-26 №46);
  // масло+запчасти — общий «Итого» = partsTotal (rev. 2026-09-30 №52)

  // Услуги к показу: авто-услуга первой строкой, если есть в заказе (§1 п.5)
  const displayServices = (() => {
    const auto = draft.services.filter(l => isAutoServiceName(l.name));
    const rest = draft.services.filter(l => !isAutoServiceName(l.name));
    return [...auto, ...rest];
  })();

  // Ни одна услуга не заполнена → первая строка красным фоном (напоминание, §1 п.5)
  const hasServices = draft.services.some(l => !isEmptyLine(l));

  // ── Бейджи и кнопки очереди (rev. 2026-09-10: «На посту» упразднён) ───────
  const queueBadge = () => {
    if (isNew) return null;
    // Проведённый заказ (readOnly) — без бейджа «Не в очереди» (OilChangePage.md §4 rev. №28)
    if (readOnly) return null;
    if (queueStatus === 'queued') return <Tag color="blue">В очереди{queuePosition != null ? `, № ${queuePosition}` : ''}</Tag>;
    return <Tag>Не в очереди</Tag>;
  };

  const queueButtons = () => {
    if (isNew || readOnly || blocked) return null;
    // «Убрать из очереди» — в меню «⋯»; здесь только основное действие
    if (queueStatus === 'queued') {
      return (
        <Button size="small" type="primary" loading={queueBusy}
          onClick={() => queueAction(finishOrder, 'Завершён', null)}>
          Завершить
        </Button>
      );
    }
    return (
      <Button size="small" loading={queueBusy}
        onClick={() => queueAction(enqueueOrder, 'Поставлен в очередь', 'queued')}>
        Поставить в очередь
      </Button>
    );
  };

  // ── Редакторы позиций (одна логика с ЗН; компоновка компактная) ──────────
  // Контрол количества/объёма строки — зеркало ServiceOrderPage
  const renderQtyControl = (l: DraftLine) => {
    // Инфо-подстрока разлива (rev. 2026-09-12 №23): «Доступно: X л» —
    // относится ко всему товару, рендерится сразу под мастером (первый ребёнок)
    if (isInfoRow(l)) {
      return (
        <div style={{ fontSize: 11, color: '#8c8c8c', whiteSpace: 'nowrap' }}>
          {l.available_marks != null ? `Доступно: ${fmtLitersFixed(bulkMasterMaxQty(l))} ${displayUnit(l.unit)}` : ''}
        </div>
      );
    }
    // Подстрока штучной марки (rev. №25): Кол-во пустое — удаление только
    // крестом MarkCodeLink в колонке товара (марка в буфере)
    if (l.parent_key && !isVolumeUnit(l.unit)) {
      return null;
    }
    // Подстрока марки разлива (rev. 2026-09-13 №26): марка и её объём — только
    // текстом; правка/удаление/подбор марок разлива — только в полном виде ЗН
    if (l.parent_key) {
      return (
        <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          {fmtLiters(l.quantity)} {displayUnit(l.unit)}
        </Text>
      );
    }
    // Мастер штучного маркированного товара: количество = число марок
    if (l.is_piece_master) {
      return <Text>{l.children?.length ?? l.quantity} шт</Text>;
    }
    // Мастер разливного товара: объём с авто-распределением по маркам.
    // «Доступно: X л» — на инфо-подстроке под мастером (rev. 2026-09-12 №23)
    if (l.is_bulk_master) {
      const maxQty = bulkMasterMaxQty(l);
      return (
        <InputNumber
          id={`qty-${l.key}`}
          min={0} max={maxQty} step={0.1} precision={1} decimalSeparator="," value={l.quantity || null}
          onChange={(val) => changeMasterQty(l.key, Number(val) || 0)}
          disabled={readOnly}
          style={{ width: 90 }}
        />
      );
    }
    // Обычная строка: шт — целые, литры — дробные
    const piece = isPieceUnit(l.unit);
    const isMarkedPiece = !!(l.marking_code && piece);
    return (
      <InputNumber
        id={`qty-${l.key}`}
        min={piece ? 1 : 0} step={piece ? 1 : 0.1} precision={piece ? 0 : 1}
        decimalSeparator={piece ? undefined : ','}
        value={l.quantity || null}
        onChange={(val) => updateLine('part', l.key, { quantity: Number(val) || (piece ? 1 : 0) })}
        disabled={readOnly || isMarkedPiece}
        style={{ width: 80 }}
      />
    );
  };

  const renderPriceControl = (l: DraftLine) => {
    if (l.parent_key) return null; // цена у мастер-строки
    return (
      <div>
        <InputNumber
          min={0} step={50} precision={2} decimalSeparator="," value={l.price || null} disabled={readOnly}
          onChange={(val) => updateLine('part', l.key, { price: Number(val) })}
          style={{ width: 80 }}
        />
        {/* Подсказка «Рек.» при отклонении от рекомендованной цены — как в ЗН
            (rev. 2026-09-12 №21): красная выше рек., зелёная ниже */}
        {l.recommended_price !== undefined && l.recommended_price !== 0 && l.recommended_price !== l.price && (
          <div style={{ fontSize: 11, color: l.price > l.recommended_price ? '#ff4d4f' : '#52c41a', marginTop: 2 }}>
            Рек.: {l.recommended_price.toLocaleString('ru-RU')} ₽
          </div>
        )}
      </div>
    );
  };

  // Выпадайка селектора: первый пункт — «Убрать» (удаление позиции из строки)
  const withRemoveAction = (menu: React.ReactNode, onRemove: (() => void) | null) => (
    <>
      {onRemove && (
        <Button type="text" danger size="small" style={{ width: '100%', textAlign: 'left' }}
          onClick={onRemove}>
          Убрать
        </Button>
      )}
      {menu}
    </>
  );

  // ── Секция «Моторное масло» ───────────────────────────────────────────────
  // Селект — ячейка строки таблицы (как в запчастях): наименование не дублируется
  // отдельным блоком (rev. 2026-09-09, OilChangePage.md §3А).
  // Заголовок секции не выводится — имя несёт шапка колонки «Масло» (rev. 2026-09-11 №6)
  const renderOilSection = () => {
    // Марки штучного товара — внутри основной строки, не отдельными <tr> (rev. 2026-09-11 №18);
    // мастер разлива получает инфо-подстроку первым ребёнком (rev. 2026-09-12 №23)
    // Марки — компактные подстроки мастера (rev. 2026-09-13 №25): children
    // остаются в dataSource, мастер однострочный
    // Хвостовой плейсхолдер-селектор после заполненных строк — добавление второго
    // и последующих масел (rev. №32); в readOnly/blocked плейсхолдера нет
    const rows: DraftLine[] = (readOnly || blocked
      ? (oilLines.length > 0 ? oilLines : [oilPlaceholder])
      : [...oilLines, oilPlaceholder]
    ).map(withBulkInfoRow);
    const isPlaceholder = (l: DraftLine) => l.key === oilPlaceholder.key;
    return (
      <div style={{ marginBottom: 16 }}>
        <Table
          size="small"
          tableLayout="fixed"
          rowKey="key"
          pagination={false}
          showHeader={true}
          dataSource={rows}
          rowClassName={(l: DraftLine) => (isInfoRow(l) ? 'order-info-subrow' : l.parent_key ? 'order-mark-subrow' : '')}
          className="order-no-expand-icon"
          expandable={{
            // Марки всегда раскрыты: expandedRowKeys управляемые — async-догрузка марок
            // после mount не раскрывалась defaultExpandAllRows, а иконка «+» упразднена
            expandedRowKeys: rows.filter(r => r.children && r.children.length > 0).map(r => r.key),
            rowExpandable: (r: DraftLine) => !!(r.children && r.children.length > 0),
            showExpandColumn: false,
          }}
          columns={[
            {
              title: (
                <>
                  Масло
                  {oilLines.length === 0 && (
                    <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}> (свое масло)</Text>
                  )}
                </>
              ),
              key: 'item',
              render: (_: unknown, l: DraftLine) => {
                // Инфо-подстрока разлива: ячейка товара пустая (rev. 2026-09-12 №23)
                if (isInfoRow(l)) return null;
                // Подстрока марки (rev. 2026-09-13 №25): штучная — MarkCodeLink
                // (клик → DataMatrix, крест при марке в буфере), разливная — код
                if (l.parent_key) {
                  if (!isVolumeUnit(l.unit)) {
                    return (
                      <MarkCodeLink
                        markingCode={l.marking_code!}
                        inBuffer={bufferMarkingCodes.has(l.marking_code!)}
                        hasFullCode={l.mark_has_full_code ?? undefined}
                        onRemove={!readOnly && !blocked ? () => removePieceMark(l.parent_key!, l.key) : undefined}
                      />
                    );
                  }
                  const inBuf = l.marking_code ? bufferMarkingCodes.has(l.marking_code) : false;
                  const incomplete = l.mark_has_full_code === false;
                  // Rev. 2026-09-22 №42: марка «в порядке» — зелёная (#52c41a),
                  // а не серая: цвет = индикатор «с маркой всё ок»
                  return (
                    <Text
                      style={{
                        fontSize: 12, wordBreak: 'break-all',
                        color: incomplete ? '#ff4d4f' : inBuf ? '#fa8c16' : '#52c41a',
                        ...(inBuf && !incomplete ? {
                          display: 'inline-block',
                          animation: 'glow-pulse-orange 1.5s ease-in-out infinite',
                          outline: '2px solid #faad14',
                          outlineOffset: '1px',
                          borderRadius: 4,
                        } : {}),
                      }}
                      title={
                        incomplete
                          ? 'Марка без криптохвоста — отсканируйте товар сканером, иначе заказ не сохранить'
                          : inBuf ? 'Марка есть в буфере сканирования' : undefined
                      }
                    >
                      Марка: {l.marking_code}
                    </Text>
                  );
                }
                // Мастер штучного маркированного товара (rev. 2026-09-13 №25):
                // однострочный — статичное имя + иконка, марки — подстроками ниже
                if (l.is_piece_master) {
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
                      <OilCanIcon ourOil />
                      <Text>{l.name}</Text>
                    </div>
                  );
                }
                return (
                  <>
                    {/* Иконка тары вплотную слева от поля (rev. 2026-09-11 №17):
                        бочка — разлив, канистра зелёная — наше штучное,
                        синяя — масло не выбрано («свое масло») */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
                      {isPlaceholder(l) ? (
                        <OilCanIcon ourOil={false} />
                      ) : l.is_bulk_master ? (
                        <OilBarrelIcon />
                      ) : (
                        <OilCanIcon ourOil />
                      )}
                      <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                      <Select
                        showSearch
                        placeholder="Выберите масло"
                        value={isPlaceholder(l) ? null : (l.external_1c_id ?? undefined)}
                        onChange={(v) => handleOilChange(v as string | undefined, l.key)}
                        onInputKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.target as HTMLInputElement).getAttribute('aria-expanded') === 'true') {
                            (e.nativeEvent as unknown as Record<string, unknown>).__skipEnterTab = true;
                          }
                        }}
                        filterOption={false}
                        onSearch={(q) => setProductSearch(q)}
                        defaultActiveFirstOption={false}
                        disabled={readOnly}
                        suffixIcon={null}
                        dropdownRender={(menu) => withRemoveAction(
                          menu,
                          !isPlaceholder(l) && l.external_1c_id && !readOnly ? () => handleOilChange(undefined, l.key) : null,
                        )}
                        style={{ width: '100%' }}
                        options={oilSelectOptions.map(g => ({
                          label: g.label,
                          options: g.options.map(o => ({
                            ...o,
                            label: `${o.displayName || o.name} — ${(o.price ?? 0).toLocaleString('ru-RU')} ₽${o.available != null ? ` (${+o.available.toFixed(1)} ${o.unit})` : ''}`,
                          })),
                        }))}
                        optionRender={(opt) =>
                          (opt.data as any).markingExpected ? (
                            <div
                              style={{ background: '#fff1f0', margin: '-5px -12px', padding: '5px 12px' }}
                              title="Товар должен быть маркированным, но в 1С признак маркировки не установлен"
                            >
                              {(opt.data as any).label}
                              <span style={{ color: '#ff4d4f', fontSize: 11, marginLeft: 6 }}>
                                в 1С нет признака маркировки
                              </span>
                            </div>
                          ) : (
                            <>{(opt.data as any).label}</>
                          )
                        }
                        notFoundContent={warehouseOptions.length === 0 ? 'Склад не загружен — обновите снимок на странице «Склад»' : 'Не найдено'}
                      />
                      </div>
                    </div>
                    {l.is_bulk_master && l.available_marks && l.available_marks.length === 0 && (
                      <div style={{ fontSize: 11, color: '#faad14', marginTop: 2 }}>
                        Марки этого товара не найдены — отсканируйте бочку сканером
                      </div>
                    )}
                    {l.unreceived_pending && !l.parent_key && (
                      <div style={{ marginTop: 4 }}>
                        <Tag
                          color="orange"
                          style={{ marginInlineEnd: 0 }}
                          title="Марки нет в 1С (сбой ЭДО) — позиция не отправлена в 1С; дофискализация вручную после приёмки УПД"
                        >
                          ожидает оприходования
                        </Tag>
                      </div>
                    )}
                  </>
                );
              },
            },
            {
              title: 'Кол-во', key: 'qty', width: 120, align: 'center',
              render: (_: unknown, l: DraftLine) => {
                return isPlaceholder(l) ? null : renderQtyControl(l);
              },
            },
            {
              title: 'Цена, ₽', key: 'price', width: 120, align: 'center',
              render: (_: unknown, l: DraftLine) => {
                // Инфо-подстрока разлива (rev. 2026-09-12 №23): «по прайсу: X ₽»
                // под колонкой «Цена» — только при расхождении с прайсом
                if (isInfoRow(l)) {
                  const infoDiffers = !!l.price_from_price_list
                    && l.recommended_price !== undefined
                    && l.recommended_price !== 0 && l.recommended_price !== l.price;
                  return infoDiffers ? (
                    <Text type="warning" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                      по прайсу: {l.recommended_price!.toLocaleString('ru-RU')} ₽
                    </Text>
                  ) : null;
                }
                if (isPlaceholder(l) || l.parent_key) return null;
                // Цена из клиентского прайса — read-only, защита от случайной
                // правки согласованной цены (rev. 2026-09-12 №19, схема §3А).
                // Пометка «по прайсу» — только при расхождении (rev. №22): у мастера
                // разлива выводится на инфо-подстроке (№23), у штучного — в ячейке
                if (l.price_from_price_list) {
                  const differs = !l.is_bulk_master
                    && l.recommended_price !== undefined
                    && l.recommended_price !== 0 && l.recommended_price !== l.price;
                  return (
                    <Tooltip title="Цена из клиентского прайса — изменение через полный вид заказ-наряда">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', whiteSpace: 'nowrap' }}>
                        <span>{l.price.toLocaleString('ru-RU')} ₽</span>
                        {differs && (
                          <Text type="warning" style={{ fontSize: 11 }}>
                            по прайсу: {l.recommended_price!.toLocaleString('ru-RU')} ₽
                          </Text>
                        )}
                      </div>
                    </Tooltip>
                  );
                }
                return renderPriceControl(l);
              },
            },
            {
              title: 'Сумма', key: 'sum', width: 80, align: 'right',
              render: (_: unknown, l: DraftLine) =>
                isPlaceholder(l) || l.parent_key ? null : <b style={{ whiteSpace: 'nowrap' }}>{lineTotal(l).toLocaleString('ru-RU')} ₽</b>,
            },
          ]}
        />
        {/* У таблицы масла своего «Итого» нет (rev. 2026-09-30 №52): масло и
            запчасти — один «Итого» под таблицей запчастей */}
      </div>
    );
  };

  // ── Таблица запчастей ─────────────────────────────────────────────────────
  // Без заголовка секции — имя несёт шапка колонки «Запчасть» (rev. 2026-09-11 №6)
  const renderPartsTable = () => {
    // Марки штучного товара — внутри основной строки, не отдельными <tr> (rev. 2026-09-11 №18);
    // мастер разлива получает инфо-подстроку первым ребёнком (rev. 2026-09-12 №23).
    // expandedRowKeys считаем из mapped-строк: у мастера без марок инфо-подстрока
    // всё равно должна быть раскрыта
    const rows = partLines.map(withBulkInfoRow);
    return (
    <div style={{ marginBottom: 16 }}>
      <Table
        size="small"
        tableLayout="fixed"
        rowKey="key"
        pagination={false}
        showHeader={true}
        dataSource={rows}
        rowClassName={(l: DraftLine) => (isInfoRow(l) ? 'order-info-subrow' : l.parent_key ? 'order-mark-subrow' : '')}
        className="order-no-expand-icon"
        expandable={{
          expandedRowKeys: rows.filter(r => r.children && r.children.length > 0).map(r => r.key),
          rowExpandable: (r: DraftLine) => !!(r.children && r.children.length > 0),
          showExpandColumn: false,
        }}
        locale={{ emptyText: 'Нет позиций' }}
        columns={[
          {
            title: 'Запчасть', key: 'item',
            render: (_: unknown, l: DraftLine) => {
              // Инфо-подстрока разлива: ячейка товара пустая (rev. 2026-09-12 №23)
              if (isInfoRow(l)) return null;
              // Подстрока марки (rev. 2026-09-13 №25): штучная — MarkCodeLink,
              // разливная — код с подсветкой буфера
              if (l.parent_key) {
                if (!isVolumeUnit(l.unit)) {
                  return (
                    <MarkCodeLink
                      markingCode={l.marking_code!}
                      inBuffer={bufferMarkingCodes.has(l.marking_code!)}
                      hasFullCode={l.mark_has_full_code ?? undefined}
                      onRemove={!readOnly && !blocked ? () => removePieceMark(l.parent_key!, l.key) : undefined}
                    />
                  );
                }
                const inBuf = l.marking_code ? bufferMarkingCodes.has(l.marking_code) : false;
                const incomplete = l.mark_has_full_code === false;
                // Rev. 2026-09-22 №42: марка «в порядке» — зелёная (#52c41a)
                return (
                  <Text
                    style={{
                      fontSize: 12, wordBreak: 'break-all',
                      color: incomplete ? '#ff4d4f' : inBuf ? '#fa8c16' : '#52c41a',
                      ...(inBuf && !incomplete ? {
                        display: 'inline-block',
                        animation: 'glow-pulse-orange 1.5s ease-in-out infinite',
                        outline: '2px solid #faad14',
                        outlineOffset: '1px',
                        borderRadius: 4,
                      } : {}),
                    }}
                    title={
                      incomplete
                        ? 'Марка без криптохвоста — отсканируйте товар сканером, иначе заказ не сохранить'
                        : inBuf ? 'Марка есть в буфере сканирования' : undefined
                    }
                  >
                    Марка: {l.marking_code}
                  </Text>
                );
              }
              // Мастер штучного маркированного товара (rev. 2026-09-13 №25):
              // однострочный — имя, марки — подстроками ниже
              if (l.is_piece_master) {
                return <Text>{l.name}</Text>;
              }
              // Мастер разлива: товар фиксирован, объём и марки — в колонке количества
              // (rev. №26: подбор марок «⋯» убран — только в полном виде ЗН)
              if (l.is_bulk_master) {
                return (
                  <Space size={4}>
                    <Text>{l.name}</Text>
                    {!readOnly && (
                      <Button size="small" type="text" danger icon={<DeleteOutlined />}
                        onClick={() => removeLine('part', l.key)} />
                    )}
                  </Space>
                );
              }
              return (
                <div style={{ display: 'flex', gap: 4, alignItems: 'center', width: '100%' }}>
                  <Select
                    showSearch filterOption={false}
                    defaultActiveFirstOption={false}
                    placeholder="Склад / 1С"
                    style={{ flex: 1, minWidth: 0 }}
                    disabled={readOnly}
                    dropdownRender={(menu) => withRemoveAction(
                      menu,
                      l.external_1c_id && !readOnly ? () => removeLine('part', l.key) : null,
                    )}
                    value={l.external_1c_id ?? undefined}
                    onSearch={(q) => { setProductSearch(q); debouncedLoadProducts(q); }}
                    onDropdownVisibleChange={(v) => { if (v) { setProductSearch(''); loadProducts(''); } }}
                    onChange={(v) => {
                      if (v == null) {
                        if (!isEmptyLine(l)) removeLine('part', l.key);
                        return;
                      }
                      pickPart(l.key, v as string);
                    }}
                    options={groupedPartOptions}
                    optionRender={(opt) =>
                      (opt.data as any).markingExpected ? (
                        <div
                          style={{ background: '#fff1f0', margin: '-5px -12px', padding: '5px 12px' }}
                          title="Товар должен быть маркированным, но в 1С признак маркировки не установлен"
                        >
                          {(opt.data as any).label}
                          <span style={{ color: '#ff4d4f', fontSize: 11, marginLeft: 6 }}>
                            в 1С нет признака маркировки
                          </span>
                        </div>
                      ) : (
                        <>{(opt.data as any).label}</>
                      )
                    }
                    notFoundContent="Не найдено"
                  />
                  {l.marking_code && <Tag color="purple" style={{ marginRight: 0, flexShrink: 0 }}>марка</Tag>}
                  {l.unreceived_pending && (
                    <Tag
                      color="orange"
                      style={{ marginRight: 0, flexShrink: 0 }}
                      title="Марки нет в 1С (сбой ЭДО) — позиция не отправлена в 1С"
                    >
                      ожидает оприходования
                    </Tag>
                  )}
                </div>
              );
            },
          },
          {
            title: 'Кол-во', key: 'qty', width: 120, align: 'center',
            render: (_: unknown, l: DraftLine) => {
              // Пустая строка (позиция не выбрана) — без контролов (rev. 2026-09-11 №11)
              if (!l.parent_key && isEmptyLine(l)) return null;
              return renderQtyControl(l);
            },
          },
          {
            title: 'Цена, ₽', key: 'price', width: 100, align: 'center',
            render: (_: unknown, l: DraftLine) => (!l.parent_key && isEmptyLine(l) ? null : renderPriceControl(l)),
          },
          {
            title: 'Сумма', key: 'sum', width: 80, align: 'right',
            render: (_: unknown, l: DraftLine) =>
              l.parent_key || isEmptyLine(l) ? null : <b style={{ whiteSpace: 'nowrap' }}>{lineTotal(l).toLocaleString('ru-RU')} ₽</b>,
          },
        ]}
      />
      {/* «Итого» под таблицей запчастей — общий для масла и запчастей
          (rev. 2026-09-30 №52; до этого масло имело свой «Итого», №46) */}
      <div style={{ textAlign: 'right', marginTop: 4 }}>
        <Text strong>Итого: {partsTotal.toLocaleString('ru-RU')} ₽</Text>
      </div>
    </div>
    );
  };

  // ── Таблица услуг ─────────────────────────────────────────────────────────
  // Без заголовка секции — имя несёт шапка колонки «Услуга» (rev. 2026-09-11 №6)
  const renderServicesTable = () => (
    <div style={{ marginBottom: 16 }}>
      <Table
        size="small"
        tableLayout="fixed"
        rowKey="key"
        pagination={false}
        showHeader={true}
        dataSource={displayServices}
        rowClassName={(l) => (
          !readOnly && !hasServices && l.key === displayServices[0]?.key ? 'oil-service-missing' : ''
        )}
        locale={{ emptyText: 'Нет позиций' }}
        style={{ marginBottom: 8 }}
        columns={[
          {
            title: 'Услуга', key: 'item',
            render: (_: unknown, l: DraftLine) => {
              // Услуга задана именем, но GUID 1С не резолвился (1С недоступна) —
              // показываем имя текстом, а не пустой Select (§1 п.5, rev. 2026-09-09)
              if (l.name.trim() && !l.external_1c_id) {
                return (
                  <Space size={6}>
                    <Text>{displayServiceName(l.name)}</Text>
                    <Tag color="orange">не найдена в 1С</Tag>
                    {!readOnly && (
                      <Button size="small" type="text" danger icon={<DeleteOutlined />}
                        title="Убрать услугу"
                        onClick={() => removeLine('service', l.key)} />
                    )}
                  </Space>
                );
              }
              // Услуги — только из каталога 1С, значение = GUID
              // (иначе в 1С строка без наименования, OilChangePage.md §1 п.6)
              return (
                <Select
                  showSearch allowClear filterOption={false}
                  defaultActiveFirstOption={false}
                  placeholder="Выбрать услугу из 1С"
                  style={{ width: '100%' }}
                  disabled={readOnly}
                  value={l.external_1c_id ?? undefined}
                  onSearch={debouncedLoadServices}
                  onDropdownVisibleChange={(v) => { if (v) loadServices(); }}
                  onChange={(v) => {
                    if (v == null) {
                      if (!isEmptyLine(l)) removeLine('service', l.key);
                      return;
                    }
                    pickService(l.key, v as string);
                  }}
                  options={autoServiceFirst(mergedServiceOptions).map(o => ({
                    value: o.value,
                    label: `${displayServiceName(o.name)}${o.price ? ` — ${Number(o.price).toLocaleString('ru-RU')} ₽` : ''}`,
                  }))}
                  notFoundContent="Не найдено"
                />
              );
            },
          },
          {
            title: 'Кол-во', dataIndex: 'quantity', key: 'qty', width: 80, align: 'center',
            render: (v: number, l: DraftLine) => {
              // Пустая строка (услуга не выбрана) — без контролов (rev. 2026-09-11 №11)
              if (isEmptyLine(l)) return null;
              return (
                <InputNumber
                  min={1} step={1} precision={0} value={v} disabled={readOnly}
                  onChange={(val) => updateLine('service', l.key, { quantity: Number(val) || 1 })}
                  style={{ width: '100%' }}
                />
              );
            },
          },
          {
            title: 'Цена, ₽', dataIndex: 'price', key: 'price', width: 110, align: 'center',
            render: (v: number, l: DraftLine) => {
              if (isEmptyLine(l)) return null;
              return (
                <div>
                  <InputNumber
                    id={`svc-price-${l.key}`}
                    min={0} step={50} precision={2} decimalSeparator="," value={v || null} disabled={readOnly}
                    onChange={(val) => updateLine('service', l.key, { price: Number(val) })}
                    style={{ width: '100%' }}
                  />
                  {/* Подсказка «Рек.» при отклонении от рекомендованной цены — как в ЗН
                      (rev. 2026-09-12 №21) */}
                  {l.recommended_price !== undefined && l.recommended_price !== 0 && l.recommended_price !== v && (
                    <div style={{ fontSize: 11, color: v > l.recommended_price ? '#ff4d4f' : '#52c41a', marginTop: 2 }}>
                      Рек.: {l.recommended_price.toLocaleString('ru-RU')} ₽
                    </div>
                  )}
                </div>
              );
            },
          },
          {
            title: 'Сумма', key: 'sum', width: 80, align: 'right',
            render: (_: unknown, l: DraftLine) =>
              isEmptyLine(l) ? null : (
                <b style={{ whiteSpace: 'nowrap' }}>{lineTotal(l).toLocaleString('ru-RU')} ₽</b>
              ),
          },
        ]}
      />
      {/* Сумма раздела — строка «Итого» под таблицей (rev. 2026-09-26 №46) */}
      <div style={{ textAlign: 'right', marginTop: 4 }}>
        <Text strong>Итого: {servicesTotal.toLocaleString('ru-RU')} ₽</Text>
      </div>
      <Space wrap style={{ marginTop: 4 }}>
        {QUICK_SERVICES.map(qs => (
          <Button
            key={qs.key}
            size="small"
            className="quick-service-btn"
            type={draft.services.some(l => normServiceName(l.name) === qs.name) ? 'primary' : 'dashed'}
            disabled={readOnly}
            onClick={async () => {
              if (draft.services.some(l => normServiceName(l.name) === qs.name)) return;
              setDirty(true);
              const found = await findServiceGuid(qs.name);
              const nl = {
                ...newLine(),
                name: qs.name,
                price: qs.price,
                external_1c_id: found?.id ?? null,
              };
              setDraft(d => {
                if (d.services.some(l => normServiceName(l.name) === qs.name)) return d;
                return {
                  ...d,
                  services: ensureTrailingEmpty([
                    ...d.services.filter(l => !isEmptyLine(l)),
                    nl,
                  ]),
                };
              });
              // Курсор — на «Цена» новой услуги, значение выделено (rev. №56)
              setTimeout(() => {
                const el = document.getElementById(`svc-price-${nl.key}`) as HTMLInputElement | null;
                el?.focus();
                el?.select();
              }, 0);
            }}
          >
            {qs.label}
          </Button>
        ))}
      </Space>
    </div>
  );

  // ── Колонка очереди слева (OilChangePage.md §3 rev. 2026-09-08) ───────────
  const renderQueueCard = (item: OilQueueItem) => {
    const caption = [item.vehicle, item.license_plate].filter(Boolean).join(' · ');
    const active = item.order_id === Number(id);
    return (
      <List.Item style={{ padding: '4px 0' }}>
        <Card
          size="small"
          hoverable
          style={{ width: '100%', borderColor: active ? '#91caff' : undefined, background: active ? '#e6f4ff' : undefined }}
          onClick={() => navigate(`/oil-change/${item.order_id}`)}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <Text delete={item.deletion_state === 'pending'}>
              {item.status === 'queued' && item.position != null ? `${item.position}. ` : ''}
              {caption || `Заказ #${item.order_id}`}
            </Text>
            {item.deletion_state === 'pending'
              ? <Tag color="red" style={{ marginInlineEnd: 0 }}>на удаление</Tag>
              : <OilMarkIcon ourOil={!!item.our_oil} bulk={!!item.bulk_oil} />}
          </div>
        </Card>
      </List.Item>
    );
  };

  // Все непроведённые замены масла вне очереди — список ниже queued
  // (схема §3 rev. 2026-09-11 №9); открытая заявка подсвечена бледно-синим
  const renderNotQueuedList = () => (
    <div style={{ marginTop: 16 }}>
      <Text type="secondary" style={{ fontSize: 12 }}>Не в очереди</Text>
      <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {displayNotQueuedItems.map(item => {
          const caption = [item.vehicle, item.license_plate].filter(Boolean).join(' · ');
          const active = item.order_id === Number(id);
          // Порядковый номер — по позиции в исходном списке (rev. №59):
          // при «снизу вверх» №1 оказывается внизу, направление читается
          const num = notQueuedItems.indexOf(item) + 1;
          return (
            <Card
              key={item.order_id}
              size="small"
              hoverable
              style={{
                width: '100%',
                borderColor: active ? '#91caff' : '#f0f0f0',
                // Приглушённые «не активные» карточки (rev. 2026-09-11 №15)
                background: active ? '#e6f4ff' : '#fafafa',
              }}
              onClick={() => navigate(`/oil-change/${item.order_id}`)}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <Text type={active ? undefined : 'secondary'} delete={item.deletion_state === 'pending'}>
                  {num}. {caption || `Заказ #${item.order_id}`}
                </Text>
                {item.deletion_state === 'pending'
                  ? <Tag color="red" style={{ marginInlineEnd: 0 }}>на удаление</Tag>
                  : <OilMarkIcon ourOil={!!item.our_oil} bulk={!!item.bulk_oil} />}
              </div>
            </Card>
          );
        })}
        {/* «+ Не в очереди» — видна всегда, даже при пустом списке (§3 rev. №14) */}
        {renderNewOrderCard(false, 'Новая заявка не в очереди')}
      </div>
    </div>
  );

  const renderQueueColumn = () => (
    <div style={{ width: 240, flexShrink: 0 }}>
      <Space style={{ justifyContent: 'space-between', width: '100%', marginBottom: 8 }}>
        <Text strong>Очередь</Text>
        <Button type="text" size="small" icon={<ReloadOutlined />} onClick={loadQueue} />
      </Space>
      {queueItems.length === 0 ? (
        <Empty description="Очередь пуста" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <List dataSource={displayQueueItems} renderItem={renderQueueCard} />
      )}
      {/* «+» после последней в очереди — новая заявка с постановкой в очередь (§3 rev. №14) */}
      <div style={{ marginTop: 8 }}>{renderNewOrderCard(true, 'Новая заявка в очередь')}</div>
      {renderNotQueuedList()}
    </div>
  );

  // ── «+» новой заявки (§3 rev. №14): из «Очереди» — с постановкой в очередь,
  // из «Не в очереди» — с enqueue=false; при несохранённых — подтверждение ──
  const handleNewOrder = (enqueue: boolean) => {
    const target = enqueue ? '/oil-change' : '/oil-change?enqueue=0';
    // Явный сброс (§3Д rev. №44): /oil-change → /oil-change не меняет isNew,
    // эффект сброса не перезапустится — без этого черновик оставался в «новой»
    const go = () => { resetNewOrder(); navigate(target); };
    if (!dirty) { go(); return; }
    Modal.confirm({
      title: 'Есть несохранённые изменения',
      content: 'Перейти к новой заявке без сохранения?',
      okText: 'Да, новая заявка',
      cancelText: 'Остаться',
      onOk: go,
    });
  };

  // Карточка-кнопка «+» — размером со строку очереди, но в стиле «добавить»:
  // пунктирная рамка, без заливки — не похожа на карточку заказа (§3 rev. №14)
  const renderNewOrderCard = (enqueue: boolean, title: string) => (
    <Tooltip title={title}>
      <Card
        size="small"
        hoverable
        style={{ width: '100%', border: '1px dashed #d9d9d9', background: 'transparent', boxShadow: 'none' }}
        styles={{ body: { padding: 0 } }}
        onClick={() => handleNewOrder(enqueue)}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px 0' }}>
          <PlusOutlined style={{ fontSize: 16, color: '#bfbfbf' }} />
        </div>
      </Card>
    </Tooltip>
  );

  return (
    <div>
      {/* Плавающая кнопка «Сохранить» при несохранённых изменениях (rev. 2026-09-11 №12) */}
      {dirty && !readOnly && !blocked && (
        <Button
          type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}
          style={{
            position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 1000,
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          }}
        >
          Сохранить
        </Button>
      )}
      {/* Шапка — на всю ширину, колонки начинаются на уровне поля «Автомобиль» (§3Б rev. №11) */}
      <Space style={{ justifyContent: 'space-between', width: '100%', marginBottom: 16 }} wrap>
        <Space wrap>
          <Text strong style={{ fontSize: 18 }}>Замена масла</Text>
          {order?.number && <Text type="secondary">ЗН № {order.number}</Text>}
          {order?.external_1c_cash_receipt_number && <Text type="secondary">ЧекККМ № {order.external_1c_cash_receipt_number}</Text>}
          {posted && <Tag color="green">Проведён</Tag>}
          {deletionPending && (
            <Tag color={order?.deletion_state === 'done' ? 'default' : 'red'}>
              {order?.deletion_state === 'done' ? 'удалён' : 'на удаление'}
            </Tag>
          )}
          {queueBadge()}
        </Space>
        <Space wrap>
          {queueButtons()}
          {/* «Провести и оплатить» — отдельная кнопка в шапке (OilChangePage.md №62/№63/№72) */}
          {!isNew && !deletionPending && order && (postingAvailable || fiscalizeAvailable || printAvailable || invoiceAvailable) && (
            <Button onClick={() => setPostPayOpen(true)}>Провести и оплатить</Button>
          )}
          <Dropdown
            menu={{
              items: [
                { key: 'switch', label: 'Показать в виде заказ-наряда' },
                // Прямая печать через 1С без браузерного PDF (№72)
                ...(!isNew && order?.external_1c_id
                  ? [{ key: 'print-order', label: 'Печать заказ-наряда', disabled: !printAvailable }]
                  : []),
                ...(!isNew && !readOnly && queueStatus === 'queued'
                  ? [{ key: 'dequeue', label: 'Убрать из очереди', danger: true }]
                  : []),
                // «Удалить» — двухстадийное удаление (service_order_deletion.md, №60)
                ...(!isNew && !deletionPending
                  ? [{ key: 'delete', label: 'Удалить', danger: true }]
                  : []),
                // Настройки вида — последний пункт (OilChangePage.md §3 rev. №59)
                { key: 'settings', label: 'Настройки' },
              ],
              onClick: ({ key }) => {
                if (key === 'switch') switchToServiceOrderView();
                if (key === 'print-order') printServiceOrderForm(Number(id));
                if (key === 'dequeue') queueAction(removeFromQueue, 'Убран из очереди', null);
                if (key === 'delete' && order) requestDeletionFlow(order, () => window.location.reload());
                if (key === 'settings') setSettingsOpen(true);
              },
            }}
          >
            <Button icon={<MoreOutlined />} />
          </Dropdown>
          {!readOnly && !blocked && (
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving} disabled={!dirty && !isNew}>
              Сохранить
            </Button>
          )}
        </Space>
      </Space>

      {/* Пометка на удаление — баннер + отмена (service_order_deletion.md, №60) */}
      {!isNew && order && (
        <DeletionBanner
          order={order}
          canWrite={hasPermission('orders:write')}
          onCancelled={() => window.location.reload()}
        />
      )}

      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
      {renderQueueColumn()}
      <div style={{ maxWidth: 720, flex: 1, position: 'relative', ...dimPristine }}>
      {/* Заглушка: исполнители различаются по работам (§3Г п.9, rev. №6) —
          редактирование только через полный вид ЗН; из очереди можно снять */}
      {blocked && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 10,
          background: 'rgba(255,255,255,0.88)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 80,
        }}>
          <Space direction="vertical" align="center" size={8}>
            <Text strong style={{ fontSize: 15 }}>Исполнители различаются по работам</Text>
            <Text type="secondary">Редактирование только через заказ-наряд</Text>
            <Space>
              <Button type="primary" onClick={() => navigate(`/service-orders/${id}`)}>
                Открыть заказ-наряд
              </Button>
              {queueStatus === 'queued' && (
                <Button danger loading={queueBusy}
                  onClick={() => queueAction(removeFromQueue, 'Убран из очереди', null)}>
                  Убрать из очереди
                </Button>
              )}
            </Space>
          </Space>
        </div>
      )}
      {order?.warning && (
        <div style={{ marginBottom: 12 }}>
          <Tag color="orange">{order.warning}</Tag>
        </div>
      )}

      {/* Автомобиль (справочник AR_DB → резолв в 1С, vehicle_resolver.md) + Госномер (§3В) + Пробег */}
      <div style={{ marginBottom: 12, display: 'flex', gap: 12 }}>
        <div style={{ flex: 2, minWidth: 0 }} className="oil-field-bold">
          <Text type="secondary" style={{ fontSize: 12 }}>Автомобиль</Text>
          <VehiclePicker
            ref={vehiclePickerRef}
            make={draft.vehicle_make}
            model={draft.vehicle_model}
            legacyName={draft.vehicle}
            autoFocus={isNew}
            disabled={readOnly}
            onChange={v => {
              const display = v.make ? [v.make, v.model].filter(Boolean).join(' ') : v.model;
              setDirty(true);
              setDraft(d => ({
                ...d,
                vehicle_make: v.make,
                vehicle_model: v.model,
                vehicle: display || null,
                vehicle_id: null,
              }));
            }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 110 }} className="oil-field-bold">
          <Text type="secondary" style={{ fontSize: 12 }}>Госномер</Text>
          <PlateInput
            value={draft.license_plate}
            disabled={readOnly}
            onChange={v => {
              setDirty(true);
              setDraft(d => ({ ...d, license_plate: v }));
            }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 110 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Пробег, км</Text>
          <InputNumber
            style={{ width: '100%' }}
            min={0}
            precision={0}
            placeholder="123456"
            value={draft.mileage ?? undefined}
            disabled={readOnly}
            onChange={v => {
              setDirty(true);
              setDraft(d => ({ ...d, mileage: v == null ? null : Number(v) }));
            }}
          />
        </div>
      </div>

      {/* Телефон клиента */}
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>Телефон клиента</Text>
        <Input
          placeholder="912 345 67 89"
          value={phoneInput}
          disabled={readOnly}
          onChange={e => setPhoneInput(e.target.value)}
          onBlur={handlePhoneBlur}
          onPressEnter={handlePhoneBlur}
          maxLength={20}
          suffix={
            <Button
              type="text" size="small" icon={<PlusOutlined />}
              onClick={() => navigate('/individuals')}
              title="Открыть физлица"
            />
          }
        />
        {clientCaption && (
          <Text type="secondary" style={{ fontSize: 12 }}>Клиент: {clientCaption}</Text>
        )}
      </div>

      <Divider />

      {renderOilSection()}

      <Divider style={{ margin: '16px 0' }} />

      {renderPartsTable()}

      <Divider style={{ margin: '16px 0' }} />

      {renderServicesTable()}

      {/* Исполнители (§3Г): единое поле вида под услугами,
          пишется в каждую строку услуг при сохранении */}
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>Исполнители</Text>
        <Select
          mode="multiple"
          maxCount={3}
          style={{ width: '100%' }}
          className={!readOnly && executorIds.length === 0 ? 'oil-executors-missing' : ''}
          placeholder="Не выбраны"
          value={executorIds}
          options={executorOptions}
          showSearch
          filterOption={(input, o) => (o?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          onSearch={loadPersons}
          disabled={readOnly}
          onChange={v => {
            const add: Record<string, string> = {};
            v.forEach(id => { const o = executorOptions.find(x => x.value === id); if (o) add[id] = o.label; });
            setPickedExecNames(prev => ({ ...prev, ...add }));
            setExecutorIds(v.slice(0, 3)); setDirty(true);
          }}
          open={execOpen}
          onFocus={() => { if (!readOnly) setExecOpen(true); }}
          onDropdownVisibleChange={setExecOpen}
          onInputKeyDown={(e) => executorSelectKeyDown(e, executorOptions, executorIds)}
        />
      </div>
      </div>

      {/* Сохранение без исполнителя — окно с полем исполнителей (§3Г п.8, rev. №41):
          «Сохранить» переносит выбор в форму и сохраняет; «Продолжить без
          исполнителя» — сохраняет с пустым полем */}
      <ShiftClosedModal
        open={shiftModalOpen}
        onClose={() => setShiftModalOpen(false)}
        onConfirm={() => {
          setShiftModalOpen(false);
          void doSave(undefined, true);
        }}
      />
      {/* Окно «Провести и оплатить» (меню «⋯», OilChangePage.md №62) */}
      {!isNew && order && (
        <PostAndPayModal
          open={postPayOpen}
          order={order}
          canPost={postingAvailable}
          canFiscalize={fiscalizeAvailable}
          canPrint={printAvailable}
          canInvoice={invoiceAvailable}
          onSyncCardPayment={readOnly ? undefined : async (cardPayment) => {
            // Перед цепочкой сохраняем весь актуальный черновик, не только смену
            // способа оплаты (проведённому проп не передаётся — доводка в 1С).
            return await doSave(undefined, false, cardPayment);
          }}
          onClose={(wasChanged) => {
            setPostPayOpen(false);
            if (wasChanged) window.location.reload();
          }}
        />
      )}
      {/* Настройки вида (меню «⋯» → «Настройки», §3 rev. №59) */}
      <Modal
        title="Настройки"
        open={settingsOpen}
        onCancel={() => setSettingsOpen(false)}
        footer={null}
        width={360}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <Text>Очередь масло снизу вверх</Text>
          <Switch checked={queueBottomUp} onChange={handleQueueBottomUpChange} />
        </div>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
          Включено — очередь снизу вверх (№1 внизу, новые сверху). Выключено — сверху вниз (№1 сверху).
        </Text>
      </Modal>
      <Modal
        title="Исполнитель не указан"
        open={execModalOpen}
        onCancel={() => setExecModalOpen(false)}
        footer={[
          <Button key="skip" onClick={() => { setExecModalOpen(false); void doSave(); }}>
            Продолжить без исполнителя
          </Button>,
          <Button
            key="save" type="primary" disabled={execModalIds.length === 0}
            onClick={() => {
              const ids = execModalIds.slice(0, 3);
              setExecModalOpen(false);
              setExecutorIds(ids);
              void doSave(ids);
            }}
          >
            Сохранить
          </Button>,
        ]}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>Исполнители</Text>
        <Select
          mode="multiple"
          maxCount={3}
          style={{ width: '100%' }}
          placeholder="Не выбраны"
          value={execModalIds}
          options={executorOptions}
          showSearch
          autoFocus
          filterOption={(input, o) => (o?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          onSearch={loadPersons}
          onChange={v => {
            const add: Record<string, string> = {};
            v.forEach(id => { const o = executorOptions.find(x => x.value === id); if (o) add[id] = o.label; });
            setPickedExecNames(prev => ({ ...prev, ...add }));
            setExecModalIds(v.slice(0, 3));
          }}
        />
      </Modal>

      {renderRightColumn()}
      </div>
    </div>
  );
};

export default OilChangePage;
